import {PaymentDetails, PaymentType} from "@bounce/shared/consumer/declarations";
import {
  AchPaymentForm,
  ApplePayPaymentForm,
  DebitPaymentForm,
  getShouldShowApplePay,
  handlePaymentMethodChange,
  PaymentFormCommonProps,
  PaymentFormWrapper,
  PaymentMethodOption,
  PaymentMethodSelectorWrapper,
  PaymentTermsAndConditionsV2,
  TermsAndConditionsDialog,
} from "@bounce/shared/consumer/pages/payment";
import {Divider} from "@bounce/shared/ui";
import {CircularProgress} from "@mui/material";
import {Theme} from "@mui/material/styles";
import {SxProps} from "@mui/system";
import React, {useMemo, useState} from "react";
import {useGetAccount, useGetUserQuery} from "~/api/requests";
import {binLookup} from "~/api/requests/binLookup";
import {Pages} from "~/bi-events/enum";
import {useAmplitude} from "~/contexts";
import {usePartnerUi} from "~/contexts/PartnerUiContext";
import BlockedACHDialog from "./BlockedACHDialog";
import {DEFAULT_PAYMENT_METHOD_SELECTION, resolvePaymentMethods} from "./resolvePaymentMethods";

interface PaymentTabPanelProps {
  onCardSubmit: (paymentType: PaymentType, paymentDetails: PaymentDetails, isChangePaymentMethod: boolean) => void;
  onAchSubmit: (paymentType: PaymentType, paymentDetails: PaymentDetails, isChangePaymentMethod: boolean) => void;
  closePaymentDialogHandler?: () => void;
  submitLabelId?: string;
  termsAndConditionsId?: string;
  isDashboardDialog?: boolean;
  sx?: SxProps<Theme>;
  backToDashboardButton?: boolean;
  handleBackToDashboard?: () => void;
  paymentDetails?: PaymentDetails;
  accountId?: string;
  immediatePaymentAmount?: string;
  hideAch?: boolean;
}

const ELIGIBLE_PARTNERS_FOR_CREDIT_CARD = ["bounce_lusa", "bounce_tally", "bounce_upgrade", "bounce_happy_money"];

const PaymentTabs = ({
  onCardSubmit,
  onAchSubmit,
  submitLabelId,
  termsAndConditionsId,
  isDashboardDialog = false,
  closePaymentDialogHandler,
  backToDashboardButton = false,
  handleBackToDashboard,
  paymentDetails,
  accountId,
  immediatePaymentAmount = "0.00",
  hideAch = false,
}: PaymentTabPanelProps) => {
  const {pageViewEvent, pageClickEvent} = useAmplitude();
  const {partnerDetails} = usePartnerUi();
  const {data: user, isInitialLoading: isUserLoading} = useGetUserQuery({});
  const userName = user?.name || "";
  const {data: account, isInitialLoading: isAccountLoading} = useGetAccount({accountId}, {enabled: !!accountId});
  const showCredit =
    !account?.block_credit_cards &&
    !!account?.partner_code &&
    ELIGIBLE_PARTNERS_FOR_CREDIT_CARD.includes(account.partner_code);
  const cardDetailsTitle = useMemo(() => {
    if (showCredit) {
      return "Credit/Debit Card";
    }
    return "Debit card";
  }, [account]);
  const achAllowed = useMemo(() => {
    return user && user.ach_allowed;
  }, [user]);

  const showAch = !hideAch;

  const isApplePayAvailable = getShouldShowApplePay(false, account?.is_apple_pay_eligible, account?.nuvei_partner_mid);

  const achBlockedAfterFailure = !!user?.ach_blocked_after_failure;

  const {methods, defaultMethod} = useMemo(() => {
    const canDisplay = (method: PaymentMethodOption) => {
      if (method === "ACH") return showAch;
      if (method === "ApplePay") return !!isApplePayAvailable;
      return method === "DebitCard";
    };
    const canBeDefault = (method: PaymentMethodOption) => method !== "ACH" || (!!achAllowed && !achBlockedAfterFailure);

    return resolvePaymentMethods({
      selection: account?.payment_method_selection ?? DEFAULT_PAYMENT_METHOD_SELECTION,
      canDisplay,
      canBeDefault,
    });
  }, [account?.payment_method_selection, showAch, isApplePayAvailable, achAllowed, achBlockedAfterFailure]);

  const initialPaymentMethod: PaymentMethodOption = useMemo(() => {
    if (paymentDetails?.ach) return "ACH";
    if (paymentDetails?.credit_card) return "DebitCard";
    return defaultMethod;
  }, [paymentDetails, defaultMethod]);
  const [chosenPaymentMethod, setChosenPaymentMethod] = useState<PaymentMethodOption>();
  const paymentMethod = chosenPaymentMethod ?? initialPaymentMethod;

  const handleSelectMethod = (method: PaymentMethodOption) => {
    setChosenPaymentMethod(method);
  };

  const primaryEmail: string = useMemo(() => {
    if (user?.represented) {
      return user.representation_email || "";
    }
    return user?.email || "";
  }, [user]);

  const onChangeTab = () => {
    handlePaymentMethodChange({
      selected: paymentMethod,
      onSelect: handleSelectMethod,
      achAllowed: !!achAllowed,
      closePaymentDialogHandler,
      BlockedACHDialog,
    });
  };

  const termsAndConditionsFormProps: PaymentFormCommonProps = {
    pageViewEvent,
    pageClickEvent,
    Pages,
    TermsAndConditionsDialog,
    PaymentTermsAndConditionsV2,
    partnerDetails,
  };

  if (isUserLoading || isAccountLoading) {
    return (
      <PaymentFormWrapper isDashboardDialog={isDashboardDialog}>
        <CircularProgress sx={{alignSelf: "center"}} />
      </PaymentFormWrapper>
    );
  }

  return (
    <PaymentFormWrapper isDashboardDialog={isDashboardDialog}>
      {methods.length > 1 && (
        <>
          <PaymentMethodSelectorWrapper
            selected={paymentMethod}
            onSelect={handleSelectMethod}
            achAllowed={!!achAllowed}
            showAch={showAch}
            cardDetailsTitle={cardDetailsTitle}
            applePayEligible={account?.is_apple_pay_eligible}
            merchantSiteId={account?.nuvei_partner_mid}
            onChangeTab={onChangeTab}
            methods={methods}
            achBlockedAfterFailure={achBlockedAfterFailure}
          />
          <Divider fullWidth />
        </>
      )}
      {paymentMethod === "ACH" && (
        <AchPaymentForm
          onAchSubmit={onAchSubmit}
          submitLabelId={submitLabelId}
          termsAndConditionsId={termsAndConditionsId}
          isChangePaymentMethod={isDashboardDialog}
          primaryEmail={primaryEmail}
          userName={userName}
          backToDashboardButton={backToDashboardButton}
          handleBackToDashboard={handleBackToDashboard}
          paymentDetails={paymentDetails}
          {...termsAndConditionsFormProps}
        />
      )}
      {paymentMethod === "DebitCard" && (
        <DebitPaymentForm
          type={"DebitCard"}
          onCardSubmit={onCardSubmit}
          submitLabelId={submitLabelId}
          termsAndConditionsId={termsAndConditionsId}
          isChangePaymentMethod={isDashboardDialog}
          primaryEmail={primaryEmail}
          userName={userName}
          backToDashboardButton={backToDashboardButton}
          handleBackToDashboard={handleBackToDashboard}
          paymentDetails={paymentDetails}
          showCredit={showCredit}
          accountId={accountId}
          checkBin={account?.block_credit_cards ? binLookup : undefined}
          blockCreditCards={account?.block_credit_cards}
          {...termsAndConditionsFormProps}
        />
      )}
      {paymentMethod === "ApplePay" && !!account && !!account.nuvei_partner_mid && (
        <ApplePayPaymentForm
          onCardSubmit={onCardSubmit}
          termsAndConditionsId={termsAndConditionsId}
          isChangePaymentMethod={isDashboardDialog}
          primaryEmail={primaryEmail}
          userName={userName}
          backToDashboardButton={backToDashboardButton}
          handleBackToDashboard={handleBackToDashboard}
          paymentDetails={paymentDetails}
          totalAmount={immediatePaymentAmount}
          merchantSiteId={account.nuvei_partner_mid}
          {...termsAndConditionsFormProps}
        />
      )}
    </PaymentFormWrapper>
  );
};
export default PaymentTabs;
